const asArray = (value, keys = []) => {
  if (Array.isArray(value)) {
    return value;
  }

  for (const key of keys) {
    const candidate = value?.[key];
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

const normalizePanopticFeedEntry = (feed = {}) => {
  const feedId = feed.feedId ?? feed.feed_id ?? feed.id;
  const cameraId = feed.cameraId ?? feed.camera_id ?? feed.deviceId ?? feed.device_id ?? feedId;
  const cameraName = feed.cameraName ?? feed.camera_name ?? feed.name ?? feed.label ?? cameraId;
  const rawSource = String(feed.sourceType || feed.source_type || feed.source || feed.source_key || feed.type || feed.feedType || feed.feed_type || '')
    .trim()
    .toLowerCase();
  const explicitNvrSource = Boolean(feed.isNvr || feed.is_nvr || feed.nvr === true)
    || /(^|[^a-z0-9])nvr([^a-z0-9]|$)/.test(rawSource)
    || rawSource === 'nvr_channel';
  const explicitNvrId = feed.nvrId ?? feed.nvr_id ?? '';
  const explicitNvrHost = feed.nvrHost ?? feed.nvr_host ?? '';
  const explicitNvrLabel = feed.nvrLabel ?? feed.nvr_label ?? feed.nvrName ?? feed.nvr_name ?? '';
  const confirmedNvr = explicitNvrSource && Boolean(explicitNvrId || explicitNvrHost || explicitNvrLabel);
  const nvrHost = explicitNvrHost || (confirmedNvr ? (feed.host ?? feed.ipAddress ?? feed.ip_address ?? '') : '');
  const nvrId = confirmedNvr ? (explicitNvrId || nvrHost || feed.host_ref || '') : explicitNvrId;
  const nvrLabel = confirmedNvr
    ? (explicitNvrLabel || feed.sourceLabel || feed.source_label || nvrHost || '')
    : explicitNvrLabel;
  const nvrChannel = confirmedNvr
    ? (feed.nvrChannel ?? feed.nvr_channel ?? feed.channel ?? feed.channel_id ?? feed.streamChannel ?? feed.stream_channel ?? '')
    : (feed.nvrChannel ?? feed.nvr_channel ?? '');
  const fallbackSource = confirmedNvr || feed.quality
    ? 'frigate'
    : 'panoptic';
  const sourceType = String(rawSource || fallbackSource)
    .trim()
    .toLowerCase();
  const role = feed.role || feed.feedRole || feed.feed_role || feed.quality || '';
  return {
    id: feedId,
    feedId,
    cameraId,
    cameraName,
    nvrId,
    nvrLabel,
    nvrHost,
    nvrChannel,
    quality: feed.quality || '',
    role,
    label: feed.label || feed.mediaType || feed.media_type || '',
    mediaType: feed.mediaType || feed.media_type || '',
    stable: (feed.stable ?? feed.online ?? feed.isOnline ?? feed.is_online) !== false,
    stale: Boolean(feed.stale || feed.cached || feed.cache_stale),
    snapshotCapable: (feed.snapshotCapable ?? feed.snapshot_capable ?? feed.hasSnapshot ?? feed.has_snapshot) !== false,
    source: sourceType,
    isNvr: confirmedNvr,
  };
};

const sourceLabel = (source = '') => {
  if (source === 'isapi') {
    return 'isapi';
  }
  if (source === 'onvif') {
    return 'onvif';
  }
  if (source === 'frigate') {
    return 'frigate';
  }
  return source || 'panoptic';
};

const sourceSite = (source = '') => {
  if (source === 'onvif') {
    return 'ONVIF';
  }
  if (source === 'isapi') {
    return 'ISAPI';
  }
  if (source === 'frigate') {
    return 'Frigate';
  }
  return 'PanOptic';
};

const panopticCameraKey = (feed) => [
  feed.cameraId || feed.cameraName,
  feed.nvrHost,
  feed.nvrChannel,
].filter(Boolean).join(':') || String(feed.feedId);

const isNvrFeed = (feed) => feed.isNvr === true;

const getCameraMatrixSortGroup = (camera = {}) => {
  if (camera?.isNvr) {
    return 1;
  }
  return 0;
};

const orderCameraMatrixFeeds = (left, right) => {
  const leftGroup = getCameraMatrixSortGroup(left);
  const rightGroup = getCameraMatrixSortGroup(right);
  if (leftGroup !== rightGroup) {
    return leftGroup - rightGroup;
  }

  return String(left?.name || left?.cameraName || '').localeCompare(String(right?.name || right?.cameraName || ''), 'en-GB', {
    sensitivity: 'base',
    numeric: true,
  });
};

const buildCamerasFromPanopticFeeds = (feedsPayload) => {
  const feeds = asArray(feedsPayload, ['feeds', 'items', 'results'])
    .map(normalizePanopticFeedEntry)
    .filter((feed) => feed.feedId);
  const groups = new Map();

  feeds.forEach((feed) => {
    const key = panopticCameraKey(feed);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(feed);
  });

  return [...groups.entries()].map(([key, group], index) => {
    const thumb = group.find((feed) => feed.quality === 'thumb' || feed.mediaType === 'snapshot') || group[0];
    const sourceLabels = [
      sourceLabel(thumb.source),
      thumb.nvrHost,
      thumb.nvrChannel ? `ch ${thumb.nvrChannel}` : '',
    ].filter(Boolean);

    return {
      id: thumb.cameraId || key || `panoptic-feed-${index}`,
      name: thumb.cameraName || `PanOptic feed ${index + 1}`,
      room: thumb.nvrHost || 'PanOptic node',
      site: sourceSite(thumb.source),
      status: thumb.stable ? 'online' : 'degraded',
      signal: thumb.quality === 'thumb' || thumb.mediaType === 'snapshot' ? 'Live snapshot' : 'Linked feed',
      lastSeen: 'Connected feed',
      labels: sourceLabels.slice(0, 3),
      unauthorized: false,
      feedId: thumb.feedId,
      feedOnline: thumb.stable,
      feedSource: 'panoptic-org',
      feedLabel: group.map((feed) => feed.quality || feed.label).filter(Boolean).join(', '),
      nvrId: thumb.nvrId,
      nvrLabel: thumb.nvrLabel,
      nvrChannel: thumb.nvrChannel,
      feedRole: thumb.role || thumb.quality,
      snapshotCapable: thumb.snapshotCapable,
      stale: thumb.stale,
      isNvr: thumb.isNvr,
    };
  }).sort(orderCameraMatrixFeeds);
};

const buildNvrCameraMatrices = (feedsPayload) => {
  const feeds = asArray(feedsPayload, ['feeds', 'items', 'results'])
    .map(normalizePanopticFeedEntry)
    .filter((feed) => feed.feedId && isNvrFeed(feed));
  const matrices = new Map();

  feeds.forEach((feed) => {
    const nvrKey = String(feed.nvrId || feed.nvrLabel || 'nvr-unknown');
    if (!matrices.has(nvrKey)) {
      matrices.set(nvrKey, {
        id: nvrKey,
        label: feed.nvrLabel || `NVR ${matrices.size + 1}`,
        status: feed.stable ? 'online' : 'degraded',
        stale: feed.stale,
        cameras: new Map(),
      });
    }

    const matrix = matrices.get(nvrKey);
    const status = feed.stable ? 'online' : 'degraded';
    if (status !== 'online') {
      matrix.status = 'degraded';
    }
    matrix.stale = matrix.stale || feed.stale;

    const cameraKey = panopticCameraKey(feed);
    const existing = matrix.cameras.get(cameraKey);
    const preferFeed = !existing
      || feed.quality === 'thumb'
      || feed.mediaType === 'snapshot'
      || feed.role === 'thumb';
    if (preferFeed) {
      matrix.cameras.set(cameraKey, {
        id: feed.cameraId || cameraKey,
        name: feed.cameraName || `NVR channel ${feed.nvrChannel || matrix.cameras.size + 1}`,
        room: feed.nvrChannel ? `Channel ${feed.nvrChannel}` : 'NVR channel',
        site: matrix.label,
        status,
        signal: feed.snapshotCapable ? 'NVR snapshot' : 'NVR feed',
        lastSeen: feed.stale ? 'Cached feed' : 'Connected feed',
        labels: ['nvr', feed.nvrChannel ? `ch ${feed.nvrChannel}` : '', feed.role || feed.quality || 'feed'].filter(Boolean).slice(0, 3),
        unauthorized: false,
        feedId: feed.feedId,
        feedOnline: feed.stable,
        feedSource: 'panoptic-org',
        feedLabel: feed.role || feed.quality || feed.label,
        nvrId: feed.nvrId,
        nvrLabel: feed.nvrLabel,
        nvrChannel: feed.nvrChannel,
        feedRole: feed.role || feed.quality,
        snapshotCapable: feed.snapshotCapable,
        stale: feed.stale,
      });
    }
  });

  return [...matrices.values()].map((matrix) => ({
    ...matrix,
    cameras: [...matrix.cameras.values()],
  }));
};

const mergePanopticFeedCameras = (cameras, panopticFeedsPayload) => {
  const feedCameras = buildCamerasFromPanopticFeeds(panopticFeedsPayload);
  if (!feedCameras.length) {
    return cameras;
  }

  const mergedByKey = new Map(cameras.map((camera) => [String(camera.id), camera]));
  const nameIndex = new Map(cameras.map((camera) => [String(camera.name || '').trim().toLowerCase(), camera.id]));

  feedCameras.forEach((feedCamera) => {
    const direct = mergedByKey.get(String(feedCamera.id));
    const byNameKey = nameIndex.get(String(feedCamera.name || '').trim().toLowerCase());
    const existing = direct || (byNameKey ? mergedByKey.get(String(byNameKey)) : null);
    if (existing) {
      const existingStatus = String(existing.status || '').trim().toLowerCase();
      const shouldUseFeedStatus = !existingStatus || ['offline', 'degraded', 'unknown'].includes(existingStatus);
      mergedByKey.set(String(existing.id), {
        ...existing,
        room: existing.room || feedCamera.room,
        site: existing.site || feedCamera.site,
        status: shouldUseFeedStatus ? feedCamera.status : existing.status,
        lastSeen: shouldUseFeedStatus ? feedCamera.lastSeen : existing.lastSeen,
        feedId: feedCamera.feedId,
        feedOnline: feedCamera.feedOnline,
        feedSource: feedCamera.feedSource,
        feedLabel: feedCamera.feedLabel,
        signal: feedCamera.signal,
        labels: [...new Set([...(existing.labels || []), ...(feedCamera.labels || [])])].slice(0, 3),
      });
      return;
    }
    mergedByKey.set(String(feedCamera.id), feedCamera);
  });

  return [...mergedByKey.values()].sort(orderCameraMatrixFeeds);
};

export {
  buildCamerasFromPanopticFeeds,
  buildNvrCameraMatrices,
  orderCameraMatrixFeeds,
  mergePanopticFeedCameras,
};
